import { plural } from '@cian/utils';
import {
  CRANAGE_TYPE_LABELS,
  FLOOR_TYPE_LABELS,
  GATES_TYPE_LABELS,
  INPUT_TYPE_LABELS,
  LIFT_TYPE_LABELS,
  TEMPERATURE_MODE_LABELS,
} from 'shared/constants/buildingLabels';
import { NBSP } from 'shared/constants/text';
import type { IBuildingCard } from 'shared/types/buildingCard';
import { formatArea } from 'shared/utils/format';

import { getAccessLines } from './getAccessLines';
import { getEquipmentLines } from './getEquipmentLines';
import { getInfrastructureItems } from './getInfrastructureItems';
import { getParkingLines } from './getParkingLines';

export interface IDetailsProp {
  name: string;
  /** Строки значения, по `<p>` на строку; пустая строка — пустая парковка, как в монолите */
  lines: string[];
  /** Серое примечание под значением (`parkingDetails`) */
  note: string | null;
  /** Инфраструктура: список в колонки */
  isFeatureList: boolean;
}

const getLabel = (labels: Partial<Record<string, string>>, value: string | null): string | undefined =>
  value ? labels[value] : undefined;

/** Сводка «6 лифтов, 2 эскалатора» — когда нет списка по типам */
function getLiftSummary({ totalLiftCount, travelatorCount, escalatorCount }: IBuildingCard): string[] {
  const parts = (
    [
      [totalLiftCount, ['лифт', 'лифта', 'лифтов']],
      [travelatorCount, ['траволатор', 'траволатора', 'траволаторов']],
      [escalatorCount, ['эскалатор', 'эскалатора', 'эскалаторов']],
    ] as Array<[number | null, [string, string, string]]>
  )
    .filter(([count]) => count)
    .map(([count, forms]) => `${count}${NBSP}${plural(count as number, forms)}`);

  return parts.length ? [parts.join(', ')] : [];
}

function getTemperatureLines({ temperatureModes, temperatureMode }: IBuildingCard): string[] {
  const modes = temperatureModes.length ? temperatureModes : [temperatureMode];
  const labels = modes.map(mode => getLabel(TEMPERATURE_MODE_LABELS, mode)).filter(Boolean);

  return labels.length ? [labels.join(', ')] : [];
}

/**
 * Детальные характеристики правой колонки в порядке `detail/index.html` монолита.
 * Каждая строка — по своим данным: общего условия монолита (`show_props_misc`) нет — решение пользователя
 */
export function getDetailsProps(card: IBuildingCard): IDetailsProp[] {
  const liftLines = getEquipmentLines(card.liftTypes, LIFT_TYPE_LABELS);
  const input = getLabel(INPUT_TYPE_LABELS, card.inputType);
  const gates = getLabel(GATES_TYPE_LABELS, card.gatesType);
  const floor = getLabel(FLOOR_TYPE_LABELS, card.floorType);

  const rows: Array<[string, string[], Partial<IDetailsProp>?]> = [
    ['Доступ в здание', getAccessLines(card)],
    ['Вход', input ? [input] : []],
    ['Лифт', card.liftTypes.length ? liftLines : getLiftSummary(card)],
    ['Крановое оборудование', getEquipmentLines(card.cranageTypes, CRANAGE_TYPE_LABELS)],
    ['Парковка', getParkingLines(card.parkings), { note: card.parkingDetails }],
    ['Инфраструктура', getInfrastructureItems(card.infrastructure), { isFeatureList: true }],
    ['Ворота', gates ? [gates] : []],
    ['Покрытие пола', floor ? [floor] : []],
    ['Температурный режим', getTemperatureLines(card)],
    ['Площадь открытой площадки', card.openPlatformArea ? [formatArea(card.openPlatformArea)] : []],
    ['Сетка колонн', card.columnGrid ? [card.columnGrid] : []],
    ['Арендопригодная площадь', card.rentableArea ? [formatArea(card.rentableArea)] : []],
  ];

  return rows
    .filter(([, lines]) => lines.length)
    .map(([name, lines, extra]) => ({ name, lines, note: null, isFeatureList: false, ...extra }));
}
